import { X, FileText } from "lucide-react";
import { useState, type ReactNode } from "react";
import { StyleSheet, View, Text, Pressable, Platform, ActivityIndicator } from "react-native";
import { spacing, colors, radii, typographyScale } from "../../../../styles/design-system";

export interface AttachmentChipProps {
  type: "task" | "assignment" | "file";
  name: string;
  duedate?: string | number | Date;
  removeChip?: () => void;
  isLoading?: boolean;
}

const getIcon = (type: "task" | "assignment" | "file") => {
  switch (type) {
    case "task":
      return "📋";
    case "assignment":
      return "📝";
    case "file":
      return null; // Will render FileText icon instead
  }
};

export const AttachmentChip = ({ type, name, duedate, removeChip, isLoading = false }: AttachmentChipProps) => {
  const [isHover, setIsHover] = useState(false);
  const icon = getIcon(type);

  const getIconAccessibilityLabel = () => {
    switch (type) {
      case "task":
        return "Task icon";
      case "assignment":
        return "Assignment icon";
      case "file":
        return "File icon";
    }
  };

  return (
    <View style={styles.attachmentChip}>
      {icon ? (
        <Text style={styles.icon} accessibilityLabel={getIconAccessibilityLabel()} accessibilityRole="image">
          {icon}
        </Text>
      ) : (
        <View accessibilityLabel={getIconAccessibilityLabel()} accessibilityRole="image">
          <FileText size={16} color={colors.grey[500]} />
        </View>
      )}
      <Text style={styles.name} accessibilityLabel={`Attachment: ${name}`} accessibilityRole="text">
        {name}
      </Text>
      {duedate != null && (
        <Text style={styles.duedate}>
          {`· ${new Date(duedate).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "2-digit" })}`}
        </Text>
      )}
      {isLoading && <ActivityIndicator size="small" color={colors.grey[500]} />}
      {!isLoading && removeChip != null && (
        <Pressable
          style={styles.removeIconWrapper}
          role="button"
          accessibilityLabel="Remove attachment"
          accessibilityHint="Remove this attachment from the message"
          onPress={removeChip}
          onHoverIn={() => {
            setIsHover(true);
          }}
          onHoverOut={() => {
            setIsHover(false);
          }}>
          <X size={20} color={isHover ? colors.grey[600] : colors.grey[400]} strokeWidth={3} />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  attachmentChip: {
    // @ts-ignore - works for web
    width: "fit-content",
    display: "flex",
    flexDirection: "row",
    gap: spacing.xs,
    marginBottom: spacing.xs,
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: radii.sm,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    ...Platform.select({
      ios: {
        shadowColor: colors.grey[900],
        shadowOffset: {
          width: 0,
          height: 1,
        },
        shadowOpacity: 0.16,
        shadowRadius: 2.5,
      },
      android: {
        elevation: 3,
      },
      default: {
        boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      },
    }),
  },
  icon: {
    fontSize: typographyScale.textBase.fontSize,
  },
  name: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
  },
  duedate: {
    fontSize: typographyScale.textXs.fontSize,
    color: colors.grey[500],
  },
  removeIconWrapper: {
    padding: 2,
  },
});
